import React, { useRef, useEffect, useState, useContext, useMemo } from 'react';
import '@cb/apricot/CBStepper';
import cn from 'classnames';
import { Glyph, glyphNames } from '@cb/apricot-react-glyph';
import { Icon, NumericIcon, iconNames } from '@cb/apricot-react-icon';
export const stepperStates = ['active', 'complete', 'skip', 'disabled'];
const pastTense = (state) => ({ complete: 'completed', skip: 'skipped' }[state] || state);
const simpleIcon = (state) => {
    const icon = (state &&
        {
            complete: 'check-fill',
            skip: 'circle',
            disabled: 'circle',
            active: 'disc',
        }[state]) ||
        'circle';
    return icon;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isIcon = (x) => iconNames.includes(x);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isGlyph = (x) => glyphNames.includes(x);
const StepperContext = React.createContext({ simple: false, navigational: false });
const StepWrapper = ({ navigational, state, href, handleClick, children }) => {
    return navigational ? (React.createElement("a", { href: state !== 'disabled' ? href : null, onClick: handleClick, "aria-disabled": state === 'disabled' || undefined }, children)) : (children);
};
export const GlyphStep = ({ state, icon, iconDecorative = true, label, subLabel, href = '/#', onClick = null, }) => {
    const ref = useRef(null);
    const [stepNumber, setStepNumber] = useState(0);
    const [totalSteps, setTotalSteps] = useState(0);
    // Mitra: we need a list of dependencies here
    useEffect(() => {
        const stepElements = Array.from(ref.current.parentElement.children);
        setStepNumber(1 + stepElements.indexOf(ref.current));
        setTotalSteps(stepElements.length);
    });
    const handleClick = e => {
        if (state === 'disabled' || onClick)
            e.preventDefault();
        onClick && onClick();
    };
    const { simple, navigational } = useContext(StepperContext);
    const IconComponent = (isIcon(icon) && React.createElement(Icon, { circular: !simple, name: icon, decorative: iconDecorative })) ||
        (!isIcon(icon) && isGlyph(icon) && React.createElement(Glyph, { circular: !simple, name: icon, decorative: iconDecorative }));
    return (React.createElement("li", { className: cn(state && `cb-stepper-${state}`), ref: ref },
        React.createElement(StepWrapper, { navigational: navigational, state: state, href: href, handleClick: handleClick },
            simple && React.createElement(Icon, { name: simpleIcon(state), decorative: iconDecorative }),
            icon && !simple && IconComponent,
            !icon &&
                !simple &&
                (state === 'complete' ? (React.createElement(Icon, { circular: true, name: "check-fill", decorative: iconDecorative })) : (React.createElement(NumericIcon, { decorative: iconDecorative }, stepNumber))),
            label && typeof label === 'string' ? React.createElement("p", null, label) : React.createElement("div", { className: "cb-stepper-label" }, label),
            subLabel && typeof subLabel === 'string' ? (React.createElement("p", null, subLabel)) : (React.createElement("div", { className: "cb-stepper-sub-label" }, subLabel)),
            React.createElement("span", { className: "sr-only cb-stepper-step" }, `step ${stepNumber} of ${totalSteps} ${state ? pastTense(state) : 'pending'}`))));
};
export const IconStep = ({ state, icon, iconDecorative = true, label, subLabel, href = '/#', onClick = null, }) => {
    const ref = useRef(null);
    const [stepNumber, setStepNumber] = useState(0);
    const [totalSteps, setTotalSteps] = useState(0);
    // Mitra: we need a list of dependencies here
    useEffect(() => {
        const stepElements = Array.from(ref.current.parentElement.children);
        setStepNumber(1 + stepElements.indexOf(ref.current));
        setTotalSteps(stepElements.length);
    });
    const handleClick = e => {
        if (state === 'disabled' || onClick)
            e.preventDefault();
        onClick && onClick();
    };
    const { simple, navigational } = useContext(StepperContext);
    const IconComponent = (isIcon(icon) && React.createElement(Icon, { circular: !simple, name: icon, decorative: iconDecorative })) ||
        (!isIcon(icon) && isGlyph(icon) && React.createElement(Glyph, { circular: !simple, name: icon, decorative: iconDecorative }));
    return (React.createElement("li", { className: cn(state && `cb-stepper-${state}`), ref: ref },
        React.createElement(StepWrapper, { navigational: navigational, state: state, href: href, handleClick: handleClick },
            simple && React.createElement(Icon, { name: simpleIcon(state), decorative: iconDecorative }),
            icon && !simple && IconComponent,
            !icon &&
                !simple &&
                (state === 'complete' ? (React.createElement(Icon, { circular: true, name: "check-fill", decorative: iconDecorative })) : (React.createElement(NumericIcon, { decorative: iconDecorative }, stepNumber))),
            label && typeof label === 'string' ? React.createElement("p", null, label) : React.createElement("div", { className: "cb-stepper-label" }, label),
            subLabel && typeof subLabel === 'string' ? (React.createElement("p", null, subLabel)) : (React.createElement("div", { className: "cb-stepper-sub-label" }, subLabel)),
            React.createElement("span", { className: "sr-only cb-stepper-step" }, `step ${stepNumber} of ${totalSteps} ${state ? pastTense(state) : 'pending'}`))));
};
export const NumericStep = ({ state, label, iconDecorative = true, subLabel, href = '/#', onClick = null, }) => {
    const ref = useRef(null);
    const [stepNumber, setStepNumber] = useState(0);
    const [totalSteps, setTotalSteps] = useState(0);
    // Mitra: we need a list of dependencies here
    useEffect(() => {
        const stepElements = Array.from(ref.current.parentElement.children);
        setStepNumber(1 + stepElements.indexOf(ref.current));
        setTotalSteps(stepElements.length);
    });
    const handleClick = e => {
        if (state === 'disabled' || onClick)
            e.preventDefault();
        onClick && onClick();
    };
    const { navigational } = useContext(StepperContext);
    return (React.createElement("li", { className: cn(state && `cb-stepper-${state}`), ref: ref },
        React.createElement(StepWrapper, { navigational: navigational, state: state, href: href, handleClick: handleClick },
            state === 'complete' ? (React.createElement(Icon, { circular: true, name: "check-fill", decorative: iconDecorative })) : (React.createElement(NumericIcon, { decorative: iconDecorative }, stepNumber)),
            label && typeof label === 'string' ? React.createElement("p", null, label) : React.createElement("div", { className: "cb-stepper-label" }, label),
            subLabel && typeof subLabel === 'string' ? (React.createElement("p", null, subLabel)) : (React.createElement("div", { className: "cb-stepper-sub-label" }, subLabel)),
            React.createElement("span", { className: "sr-only cb-stepper-step" }, `step ${stepNumber} of ${totalSteps} ${state ? pastTense(state) : 'pending'}`))));
};
export const SimpleStep = ({ state, iconDecorative = true, href = '/#', onClick = null, }) => {
    const ref = useRef(null);
    const [stepNumber, setStepNumber] = useState(0);
    const [totalSteps, setTotalSteps] = useState(0);
    // Mitra: we need a list of dependencies here
    useEffect(() => {
        const stepElements = Array.from(ref.current.parentElement.children);
        setStepNumber(1 + stepElements.indexOf(ref.current));
        setTotalSteps(stepElements.length);
    });
    const handleClick = e => {
        if (state === 'disabled' || onClick)
            e.preventDefault();
        onClick && onClick();
    };
    const { navigational } = useContext(StepperContext);
    return (React.createElement("li", { className: cn(state && `cb-stepper-${state}`), ref: ref },
        React.createElement(StepWrapper, { navigational: navigational, state: state, href: href, handleClick: handleClick },
            React.createElement(Icon, { name: simpleIcon(state), decorative: iconDecorative }),
            React.createElement("span", { className: "sr-only cb-stepper-step" }, `step ${stepNumber} of ${totalSteps} ${state ? pastTense(state) : 'pending'}`))));
};
export const Stepper = ({ stepperId, children, vertical, ariaLabel, navigational, simple, horizontalFix, role = navigational ? 'navigation' : 'region', className, }) => {
    const context = useMemo(() => ({ simple: !!simple, navigational: !!navigational }), [simple, navigational]);
    return (React.createElement("div", { id: stepperId, className: cn('cb-stepper', vertical && 'cb-stepper-vertical', simple && 'cb-stepper-simple', horizontalFix && 'cb-stepper-horizontal-fix', className), "aria-label": ariaLabel, "data-cbtrack-comp": "apricot-react:stepper", role: role },
        React.createElement(StepperContext.Provider, { value: context },
            React.createElement("ol", null, children))));
};
//# sourceMappingURL=Stepper.js.map